/* eslint-disable max-lines */
import { Menu } from '@material-ui/core';
import { LabelItem } from 'components/LabelsList';
import { MenuItemWrapper } from 'components/MenuButton';
import SearchInput from 'components/SearchInput';
import Checkbox from 'components/base/Checkbox';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import { verticalScroller } from 'components/base/scroller';
import GroupCheckboxIcon from 'images/icons/checkedGroupCheckbox.svg';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  JIRA_STATE_CATEGORIES_KEYS,
  LINB_STATUS_CATEGORIES_KEYS,
  PM_STATES_CATEGORIES_MAPPER,
  PM_STATES_CATEGORIES_NAMES_MAPPER,
  TRANSITION_STRATEGY
} from './EditCustomPmMetricsModal.constants';
import { SECTIONS_KEYS } from './EditCustomPmMetricsModal.service';
import {
  deduplicateStatuses,
  fetchStatusesFromDynamoDB,
  onSearchPMEntities
} from './JiraStateMenu.service';
import { useSelector } from 'react-redux';
import { pmToolSensorIdSelector } from 'state/selectors/sensors-authorizations';
import { LabelStyled } from 'components/LabelsList.style';
import { LBButton } from '@linearb/buzz';

const MENUS_KEYS = {
  JIRA_STATE: 'jiraState',
  STATE_TRANSITIONS: 'stateTransitions'
};

const StyledMenuItem = styled.li`
  padding: 1rem;
`;

const StyledButtonsMenuItem = styled.li`
  text-align: end;
`;

const ISSUE_STATES_MENU_ORDERED = [
  JIRA_STATE_CATEGORIES_KEYS.NEW,
  JIRA_STATE_CATEGORIES_KEYS.INDETERMINATE,
  JIRA_STATE_CATEGORIES_KEYS.DONE
];

const OrderWrapper = styled(Flex)`
  width: 2rem;
  height: 2rem;
  background-color: #3b5998;
  border-radius: 30px;
  color: white;
  font-size: 1.2rem;
  justify-content: center;
`;

const Scroll = styled.div`
  max-height: 30rem;
  ${verticalScroller()};
`;

const MenuButton = ({ selectButtonEnable, buttonText, setAnchorEl }) => (
  <div>
    <LBButton
      variant='outline'
      size='lg'
      data-testid='add-filters-button'
      onClick={e => {
        setAnchorEl(e.currentTarget);
      }}
      disabled={!selectButtonEnable}
    >
      <Icon color={selectButtonEnable ? 'chambray' : 'silverSand'} name='Add' />
      {buttonText}
    </LBButton>
  </div>
);

const checkIfAllStatesInCategorySelected = ({
  itemsList,
  currentSelectedItems,
  categoryKey
}) => {
  const allStatesInCategory = itemsList.filter(
    ({ statusCategory, isGroupItem = false }) =>
      _.get(statusCategory, 'key', null) === categoryKey && !isGroupItem
  );
  return allStatesInCategory.length === currentSelectedItems.length;
};

const isWithGroupCheckboxIcon = ({
  isGroupItem,
  currentSelectedItems,
  categoryKey,
  itemsList,
  stateCategoryTransition
}) => {
  return !stateCategoryTransition.length &&
    isGroupItem &&
    !checkIfAllStatesInCategorySelected({
      currentSelectedItems,
      categoryKey,
      itemsList
    })
    ? GroupCheckboxIcon
    : null;
};

const checkIfItemIsSelected = ({ currentSelectedItems = [], item = {} }) => {
  const stateName = item.name || item.title;
  const stateCategory =
    item.category ||
    PM_STATES_CATEGORIES_MAPPER[_.get(item, 'statusCategory.key')];
  const foundIndex = currentSelectedItems?.findIndex(
    ({ name: currentName, category: currentCategory }) =>
      currentName === stateName && currentCategory === stateCategory
  );

  return foundIndex !== -1;
};

const shouldShowCategory = ({
  currentSelectedItems,
  item,
  order,
  isHovered,
  isGroupItem
}) => {
  const isItemSelected = checkIfItemIsSelected({ currentSelectedItems, item });
  return !order && !isGroupItem && (isItemSelected || isHovered);
};

const MenuButtonItem = ({
  item,
  status,
  category,
  onClick,
  order,
  disabled = false,
  currentSelectedItems = [],
  categoryKey,
  isGroupItem = false,
  withCheckbox,
  itemsList,
  categoryOrder,
  stateCategoryTransition,
  isCheckboxChecked
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <MenuItemWrapper
      onClick={disabled ? null : onClick}
      width='25rem'
      height='2.8rem'
      disabled={disabled}
      data-testid={
        isGroupItem
          ? `group-menu-item-${categoryKey}`
          : `menu-item-${status}-${categoryKey}`
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex
        justify='space-between'
        width='100%'
        height='2.8rem'
        items='center'
        pl={isGroupItem ? '' : withCheckbox ? 'medium' : 'x-medium'}
      >
        <Flex items='center'>
          {withCheckbox && (
            <Checkbox
              data-testId={
                isGroupItem
                  ? `group-checkbox-${categoryKey}`
                  : `item-checkbox-${status}-${categoryKey}`
              }
              value={item}
              disabled={disabled}
              checked={isCheckboxChecked({
                status,
                categoryKey,
                isGroupItem
              })}
              checkboxIcon={isWithGroupCheckboxIcon({
                isGroupItem,
                currentSelectedItems,
                categoryKey,
                itemsList,
                stateCategoryTransition
              })}
            />
          )}
          <Text
            size='medium'
            width='13rem'
            lineHeight='1.8rem'
            color={disabled ? 'osloGray' : 'black'}
          >
            {status}
          </Text>
        </Flex>
        {shouldShowCategory({
          currentSelectedItems,
          item,
          order,
          isHovered,
          isGroupItem
        }) && (
          <Text color='nepal' textTransform='uppercase'>
            {category}
          </Text>
        )}
        {order && (
          <OrderWrapper items='center' data-testid='order-test-id'>
            {order}
          </OrderWrapper>
        )}
        {categoryOrder && (
          <OrderWrapper items='center' data-testid='category-order-test-id'>
            {categoryOrder}
          </OrderWrapper>
        )}
      </Flex>
    </MenuItemWrapper>
  );
};

const checkIfItemDisabled = ({
  index,
  key,
  statesTransition = [],
  currentSelectedItems = [],
  categoryKey,
  isGroupItem,
  stateCategoryTransition = [],
  categoryIndex,
  transitionType
}) => {
  if (key === MENUS_KEYS.STATE_TRANSITIONS) {
    const isCategoryTransitions =
      transitionType === TRANSITION_STRATEGY.CATEGORY;
    const isStateTransitions = transitionType === TRANSITION_STRATEGY.STATE;
    return (
      (isCategoryTransitions && !isGroupItem) ||
      (isStateTransitions && isGroupItem) ||
      (statesTransition.length === 2 && index === -1) ||
      (stateCategoryTransition.length === 2 && categoryIndex === -1)
    );
  } else {
    const selectedCategory = _.get(currentSelectedItems[0], 'category', null);
    return (
      selectedCategory &&
      PM_STATES_CATEGORIES_MAPPER[categoryKey] !== selectedCategory
    );
  }
};

const renderItems = ({
  key,
  menuItems,
  statesTransition = [],
  currentSelectedItems = [],
  stateCategoryTransition = [],
  transitionType = null,
  isCheckboxChecked
}) =>
  menuItems.map((item, idx) => {
    const isGroupItem = _.get(item, 'isGroupItem', false);
    const categoryName = _.get(item, 'statusCategory.name', '');
    const categoryKey = _.get(item, 'statusCategory.key', '');

    const index = statesTransition.findIndex(({ id }) => id === item.id);
    const categoryIndex = stateCategoryTransition.findIndex(
      ({ name }) => name === item.name
    );

    const isDisabled = checkIfItemDisabled({
      index,
      key,
      statesTransition,
      currentSelectedItems,
      categoryKey,
      isGroupItem,
      stateCategoryTransition,
      categoryIndex,
      transitionType
    });

    return (
      <MenuButtonItem
        key={key}
        item={item}
        status={item.title}
        category={categoryName}
        categoryKey={categoryKey}
        onClick={item.onClick}
        order={index > -1 ? index + 1 : null}
        categoryOrder={categoryIndex > -1 ? categoryIndex + 1 : null}
        disabled={isDisabled}
        currentSelectedItems={currentSelectedItems}
        isGroupItem={item.isGroupItem}
        withCheckbox={true}
        itemsList={menuItems}
        stateCategoryTransition={stateCategoryTransition}
        statesTransition={statesTransition}
        isCheckboxChecked={isCheckboxChecked}
      />
    );
  });

const groupItemFormatter = ({ categoryKey, onGroupItemClick, orderedMenu }) => {
  const categoryName = PM_STATES_CATEGORIES_NAMES_MAPPER[categoryKey];
  const item = {
    name: categoryName,
    title: categoryName,
    statusCategory: {
      name: categoryName,
      key: categoryKey
    },
    isGroupItem: true,
    onClick: () => {
      onGroupItemClick({
        categoryKey,
        orderedMenu
      });
    }
  };
  return item;
};

const menuFormat = ({
  searchResultItemsList,
  itemKey = 'id',
  onItemClick,
  onGroupItemClick = _.noop,
  chooseCategoryEnable = false
}) => {
  const menuItems = searchResultItemsList.map(
    ({ name, [itemKey]: id, statusCategory }) => ({
      title: name,
      id,
      statusCategory,
      onClick: () => {
        onItemClick({
          id,
          name,
          title: name,
          category: _.get(
            PM_STATES_CATEGORIES_MAPPER,
            _.get(statusCategory, 'key'),
            LINB_STATUS_CATEGORIES_KEYS.CREATED
          )
        });
      }
    })
  );

  const sortedMenu = _.sortBy(menuItems, 'title', []);
  const orderedMenu = _.sortBy(
    sortedMenu,
    item => {
      const category = _.get(item, 'statusCategory.key', '');
      return ISSUE_STATES_MENU_ORDERED.indexOf(category);
    },
    []
  );
  // TODO: change the implementation of the menu to hierarchial implementation when category is the father and states are the sons
  if (chooseCategoryEnable) {
    ISSUE_STATES_MENU_ORDERED.map(categoryKey => {
      const item = groupItemFormatter({
        categoryKey,
        onGroupItemClick,
        orderedMenu
      });
      const index = orderedMenu.findIndex(
        ({ statusCategory }) => _.get(statusCategory, 'key', '') === categoryKey
      );
      orderedMenu.splice(index, 0, item);
    });
  }
  return orderedMenu;
};

const SelectedStateLabel = ({
  values,
  onDelete,
  titleKey = 'title',
  statuses
}) => {
  const sensorId = useSelector(pmToolSensorIdSelector);
  const title = SECTIONS_KEYS[sensorId]?.ISSUE_STATE;
  return (
    <LabelStyled items='center' wrap='wrap' width='fit-content'>
      <LabelItem
        titleKey={titleKey}
        onDelete={(_, id) => onDelete(id)}
        labelItem={{
          key: title,
          label: title,
          values
        }}
        allStatuses={statuses}
      />
    </LabelStyled>
  );
};

export const StateTransitionMenu = ({
  buttonText,
  itemsList = [],
  setSelectedItems,
  titleKey = 'title',
  pmData = {},
  selectedItems
}) => {
  const [anchorEl, setAnchorEl] = useState();
  const [currentSelectedTransition, setCurrentSelectedTransition] = useState(
    {}
  );
  const [searchResultItemsList, setSearchResultItemsList] = useState(itemsList);
  const [statesTransition, setStatesTransition] = useState([]);
  const [stateCategoryTransition, setStateCategoryTransition] = useState([]);
  const [transitionType, setTransitionType] = useState(null);
  const [allStatuses, setAllStatuses] = useState([]);

  const isCheckboxChecked = item => {
    const { isGroupItem, categoryKey, status } = item;
    if (isGroupItem) {
      return !!stateCategoryTransition.find(item => item.name === status);
    } else {
      return !!(
        !!statesTransition.find(item => item.name === status) ||
        stateCategoryTransition.find(
          item => item.key === PM_STATES_CATEGORIES_MAPPER[categoryKey]
        )
      );
    }
  };

  // update transition strategy whenever clicking on an item
  useEffect(() => {
    if (statesTransition.length) {
      setTransitionType(TRANSITION_STRATEGY.STATE);
    } else if (stateCategoryTransition.length) {
      setTransitionType(TRANSITION_STRATEGY.CATEGORY);
    } else {
      setTransitionType(null);
    }
  }, [statesTransition, stateCategoryTransition]);

  const isSaveEnable =
    statesTransition.length === 2 || stateCategoryTransition.length === 2;

  const formatTransition = transition => {
    const transitionLabel = `${_.get(transition, 'from.name')} > ${_.get(
      transition,
      'to.name'
    )}`;
    return {
      name: transitionLabel,
      id: transitionLabel,
      title: transitionLabel
    };
  };

  useEffect(() => {
    if (_.isEmpty(selectedItems)) {
      setCurrentSelectedTransition({});
    } else {
      setCurrentSelectedTransition(formatTransition(selectedItems));
    }
  }, [selectedItems]);

  useEffect(() => {
    (async () => {
      const data = await fetchStatusesFromDynamoDB({ fetchAll: true });
      // here we are deduplicating statuses with same name and category
      const uniqueItems = deduplicateStatuses(data);
      setSearchResultItemsList(uniqueItems);
      setAllStatuses(data);
    })();
  }, []);

  const closeMenu = () => {
    setStateCategoryTransition([]);
    setStatesTransition([]);
    setAnchorEl(null);
  };

  const deleteTransition = id => {
    setSelectedItems({});
    setStatesTransition([]);
    setStateCategoryTransition([]);
  };

  const onItemClick = item => {
    const foundIndex = statesTransition.findIndex(
      ({ id, category }) => id === item.id && category === item.category
    );
    if (foundIndex > -1) {
      const newStatesTransition = statesTransition.filter(
        ({ id, category }) => id !== item.id || category !== item.category
      );
      setStatesTransition(newStatesTransition);
    } else {
      setStatesTransition([...statesTransition, { ...item }]);
    }
    setStateCategoryTransition([]);
  };

  const onGroupItemClick = ({ categoryKey }) => {
    const clickedCategory = PM_STATES_CATEGORIES_MAPPER[categoryKey];
    const categoryName = PM_STATES_CATEGORIES_NAMES_MAPPER[categoryKey];
    const index = stateCategoryTransition.findIndex(
      ({ key }) => key === clickedCategory
    );
    if (index > -1) {
      // category already been selected
      const newStateCategoryTransition = stateCategoryTransition.filter(
        ({ key }) => key !== clickedCategory
      );
      setStateCategoryTransition(newStateCategoryTransition);
    } else {
      setStateCategoryTransition([
        ...stateCategoryTransition,
        { name: categoryName, key: clickedCategory }
      ]);
    }
    setStatesTransition([]);
  };

  const menuItems = menuFormat({
    searchResultItemsList,
    onItemClick,
    onGroupItemClick,
    chooseCategoryEnable: true
  });

  // For organizations with jira on prem we doing backend search
  const onSearch = async searchValue => {
    const filteredItems = await onSearchPMEntities({ searchValue });

    setSearchResultItemsList([...filteredItems]);
  };

  const saveTransition = transition => {
    const transitionToLabel = `${transition[0].name} > ${transition[1].name}`;
    setSelectedItems({
      from: transition[0],
      to: transition[1],
      id: transitionToLabel,
      name: transitionToLabel,
      title: transitionToLabel,
      transitionType
    });
    closeMenu();
  };
  const selectButtonEnable = _.isEmpty(currentSelectedTransition);

  return (
    <>
      <div className='my-4 flex max-w-full flex-wrap gap-2'>
        <Flex mb='small' maxWidth='100%' wrap='wrap'>
          {_.get(currentSelectedTransition, 'name', null) && (
            <SelectedStateLabel
              values={[currentSelectedTransition]}
              onDelete={deleteTransition}
              titleKey={titleKey}
              statuses={allStatuses}
            />
          )}
          <MenuButton
            selectButtonEnable={selectButtonEnable}
            setAnchorEl={setAnchorEl}
            buttonText={buttonText}
          />
        </Flex>
      </div>
      <Flex maxWidth='3rem'>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)}>
          <StyledMenuItem>
            <SearchInput
              placeholder='Search'
              width='unset'
              height='2.6rem'
              onChange={value => onSearch(value)}
            />
          </StyledMenuItem>
          <Scroll>
            {renderItems({
              menuItems,
              statesTransition,
              stateCategoryTransition,
              key: MENUS_KEYS.STATE_TRANSITIONS,
              transitionType,
              isCheckboxChecked
            })}
          </Scroll>

          <StyledButtonsMenuItem>
            <div className='flex flex-row gap-1'>
              <LBButton onClick={closeMenu} variant='outline'>
                Close
              </LBButton>
              <LBButton
                data-testid='save-transition-button'
                onClick={() => {
                  if (statesTransition.length > 0) {
                    saveTransition(statesTransition);
                  } else {
                    saveTransition(stateCategoryTransition);
                  }
                }}
                disabled={!isSaveEnable}
              >
                Save
              </LBButton>
            </div>
          </StyledButtonsMenuItem>
        </Menu>
      </Flex>
    </>
  );
};

export const JiraStateMenu = ({
  buttonText,
  selectedItems = [],
  setSelectedItems,
  maxSelected = null,
  titleKey = 'title'
}) => {
  const [anchorEl, setAnchorEl] = useState();
  const [searchResultItemsList, setSearchResultItemsList] = useState([]);
  const [currentSelectedItems, setCurrentSelectedItems] =
    useState(selectedItems);
  const [categoryGroupSelected, setCategoryGroupSelected] = useState(null);
  const [allStatuses, setAllStatuses] = useState([]);

  useEffect(() => {
    (async () => {
      const data = await fetchStatusesFromDynamoDB({ fetchAll: true });
      // here we are deduplicating statuses with same name and category
      const uniqueItems = deduplicateStatuses(data);
      setSearchResultItemsList(uniqueItems);
      setAllStatuses(data);
    })();
  }, []);

  const isCheckboxChecked = item => {
    const { status, categoryKey, isGroupItem } = item;

    if (isGroupItem) {
      return (
        currentSelectedItems[0]?.category ===
        PM_STATES_CATEGORIES_MAPPER[categoryKey]
      );
    } else {
      return !!currentSelectedItems.find(
        item =>
          item.name === status &&
          item.category === PM_STATES_CATEGORIES_MAPPER[categoryKey]
      );
    }
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setCurrentSelectedItems(selectedItems);
  };

  const deleteItem = id => {
    const filteredList = selectedItems.filter(
      ({ id: currentItemId }) => currentItemId !== id
    );
    setSelectedItems(filteredList);
    setCurrentSelectedItems(filteredList);
  };

  const saveItems = () => {
    setSelectedItems([...currentSelectedItems]);
    closeMenu();
  };

  const onGroupItemClick = ({ categoryKey, orderedMenu }) => {
    if (categoryGroupSelected !== categoryKey) {
      setCategoryGroupSelected(categoryKey);
      const newSelectedItems = orderedMenu
        .filter(
          ({ statusCategory, isGroupItem = false }) =>
            _.get(statusCategory, 'key', null) === categoryKey && !isGroupItem
        )
        .map(item => ({
          id: item.id,
          name: item.title,
          title: item.title,
          category: _.get(PM_STATES_CATEGORIES_MAPPER, item.statusCategory.key)
        }));
      setCurrentSelectedItems(newSelectedItems);
    } else {
      setCategoryGroupSelected(null);
      setCurrentSelectedItems([]);
    }
  };

  const onItemClick = item => {
    if (checkIfItemIsSelected({ currentSelectedItems, item })) {
      setCurrentSelectedItems(
        currentSelectedItems.filter(
          ({ name: currentName, category: currentCategory }) =>
            !(currentName === item.name && currentCategory === item.category)
        )
      );
    } else {
      setCurrentSelectedItems([...currentSelectedItems, { ...item }]);
      const currentCategoryKey = Object.keys(PM_STATES_CATEGORIES_MAPPER).find(
        key => PM_STATES_CATEGORIES_MAPPER[key] === item.category
      );
      setCategoryGroupSelected(currentCategoryKey);
    }
  };

  const menuItems = menuFormat({
    searchResultItemsList,
    onItemClick,
    onGroupItemClick,
    chooseCategoryEnable: true
  });

  const selectButtonEnable =
    !maxSelected || selectedItems?.length < maxSelected;

  const onSearch = async searchValue => {
    const filteredItems = await onSearchPMEntities({ searchValue });
    setSearchResultItemsList([...filteredItems]);
  };

  return (
    <>
      <div className='my-4 flex max-w-full flex-wrap gap-2'>
        {selectedItems?.length ? (
          <SelectedStateLabel
            values={selectedItems}
            onDelete={deleteItem}
            titleKey={titleKey}
            statuses={allStatuses}
          />
        ) : (
          <></>
        )}
        <MenuButton
          selectButtonEnable={selectButtonEnable}
          setAnchorEl={setAnchorEl}
          buttonText={buttonText}
        />
      </div>
      <div className='max-w-3rem'>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)}>
          <StyledMenuItem>
            <SearchInput
              placeholder='Search'
              width='unset'
              height='2.6rem'
              onChange={value => onSearch(value)}
            />
          </StyledMenuItem>
          <Scroll>
            {renderItems({
              menuItems,
              currentSelectedItems,
              key: MENUS_KEYS.JIRA_STATE,
              isCheckboxChecked
            })}
          </Scroll>

          <StyledButtonsMenuItem>
            <LBButton onClick={closeMenu} variant='outline'>
              Close
            </LBButton>

            <LBButton onClick={saveItems}>Add</LBButton>
          </StyledButtonsMenuItem>
        </Menu>
      </div>
    </>
  );
};
